// SuppliersList.jsx — proc-v3 (F2.3c): PRIMA VISTA DEL MONOLITE in React,
// lista "Fornitori", flag-gated (shell_proc_v3_suppliers, default OFF).
//
// Replica 1:1 renderSuppliers di proc-v0.js:6537-6646: stesso titolo/sottotitolo,
// stessi bottoni (Importa lista / + Nuovo fornitore), stessi filtri alti
// (stato, affidabilità, categoria, dormienti), stesse colonne e badge, stesso
// ordinamento di default (createdAt desc), stesso click riga → dettaglio V1.
// La tabella è il componente condiviso client/src/ui/DataTable.jsx ESTESO
// (filtri per colonna con semantica v1, evidenziazione, selezione + bulk).
//
// CONFINI (dichiarati nello studio f23c):
//  - dettaglio fornitore, modali, azioni bulk = V1 (deleghe in api.js);
//  - filtro categoria = STESSO widget v1 (makeSearchableSelect + _categoryOption)
//    dentro un div ref React: parità esatta di opzioni e comportamento;
//  - MAI 'nav:badge' da questo modulo (emettitore unico: adapter proc-v0).
//
// STATO (parità di persistenza col v1):
//  - filtri alti + selezione vivono nella FACTORY (pagina), come i binding
//    lessicali supFilters/supplierSelection del v1: sopravvivono a nav
//    via/ritorno (unmount/mount) e ai refresh dati;
//  - filtri per colonna e sort vivono nel DataTable (root React persistente):
//    sopravvivono ai refresh dati, si azzerano uscendo dalla vista
//    (deviazione 3 dichiarata: il v1 li azzera a OGNI renderSuppliers).
//
// Il chrome della pagina usa le stesse classi Tailwind del v1: il componente
// vive DENTRO il documento del monolite (#suppliers-content), dove quel CSS
// esiste già — parità visiva senza fork di stile.

import React from 'react';
import { DataTable } from '../ui/DataTable.jsx';
import {
  tLabel, TEXTS, defaultFilters, readSuppliers, fmtCreated,
  confColor, confLabel, catsCellData, tagsCellData,
  nameFilterText, statusFilterText,
  openNewSupplier, openBulkImport, openSupplierDetail,
  resolveSelection, buildBulkActions,
} from './api.js';

// Filtro categoria: RIUSO del widget v1 (globali makeSearchableSelect +
// _categoryOption) dentro un ref React. Ricreato quando cambiano opzioni o
// valore, come fa il v1 a ogni renderSuppliers.
function CategoryFilter({ win, cats, value, onChange }) {
  const ref = React.useRef(null);
  const catsSig = (cats || []).map((c) => c.path).join('|');
  React.useEffect(() => {
    const host = ref.current;
    if (!host) return;
    try {
      const widget = win.makeSearchableSelect({
        initial: value === 'all' ? '' : value,
        options: [{ value: '', label: TEXTS.anyCategory, searchText: '' }]
          .concat((cats || []).map((c) => win._categoryOption(c))),
        placeholder: TEXTS.anyCategoryPlaceholder,
        onChange: (v) => onChange(v || 'all'),
      });
      host.replaceChildren(widget.rootEl);
    } catch (e) {
      // widget v1 non disponibile: il filtro categoria resta vuoto ma la vista
      // vive — errore dichiarato in console, mai silenzio.
      console.error('[proc-v3] filtro categoria non disponibile:', e);
    }
    return () => { try { host.replaceChildren(); } catch (e) { /* già svuotato */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catsSig, value]);
  return <div id="sup-filter-cat-mount" style={{ minWidth: 200 }} ref={ref} />;
}

export function SuppliersList({ win, tick, filters, selection }) {
  // filtri alti: stato React specchiato sull'oggetto della factory (write-through)
  const [f, setFState] = React.useState(() => ({ ...filters }));
  const setF = (patch) => {
    Object.assign(filters, patch);
    setFState({ ...filters });
  };
  const [, force] = React.useReducer((n) => n + 1, 0);
  const rootRef = React.useRef(null);

  // dati: letti dallo store del monolite a ogni refresh (tick dal wrapper) o
  // cambio dei filtri alti — stessa composizione del v1 (api.readSuppliers)
  const data = React.useMemo(
    () => readSuppliers(win, f),
    [win, tick, f.qualified, f.confidence, f.category, f.sleeping],
  );

  // selezione: PRUNA gli id che non esistono più nello store (deviazione 2
  // dichiarata — il live-sync può cancellare record mentre la vista è aperta;
  // le selezionate fuori filtro restano, parità selectionResolve v1)
  React.useEffect(() => {
    let changed = false;
    for (const id of Array.from(selection)) {
      if (!win.store.getSupplier(id)) { selection.delete(id); changed = true; }
    }
    if (changed) force();
  }, [data, selection, win]);

  // parità UX v1 (proc-v0.js:16476): entrando nella vista, focus sul filtro Nome
  React.useEffect(() => {
    try {
      const el = rootRef.current && rootRef.current.querySelector('[data-filter-col="name"]');
      if (el) el.focus({ preventScroll: true });
    } catch (e) { /* focus best-effort */ }
  }, []);

  const t = (k) => tLabel(win, k);

  const columns = [
    {
      key: 'name',
      label: t('col.name'),
      sortable: true,
      filterText: nameFilterText,
      render: (r, ctx) => (
        <>
          <span className="font-medium"><ctx.Mark text={r.name} tokens={ctx.tokens} /></span>
          {r._sleepingDays !== undefined && (
            <span className="ml-2 bg-amber-100 text-amber-800 text-[10px] px-1.5 py-0.5 rounded">
              <ctx.Mark
                text={'sleeping' + (Number.isFinite(r._sleepingDays) ? ' ' + r._sleepingDays + 'd' : '')}
                tokens={ctx.tokens}
              />
            </span>
          )}
        </>
      ),
    },
    { key: '_country', label: t('col.country'), sortable: true },
    {
      key: '_statusText',
      label: t('col.status'),
      sortable: true,
      filterText: statusFilterText,
      render: (r, ctx) => (
        <span className={(r.qualified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800') + ' text-xs px-2 py-0.5 rounded'}>
          <ctx.Mark text={r.qualified ? 'qualified' : 'prospect'} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: '_confText',
      label: t('col.confidence'),
      sortable: true,
      filterText: (r) => confLabel(win, r.confidence),
      render: (r, ctx) => (
        <span className={confColor(r.confidence) + ' text-xs font-medium'}>
          <ctx.Mark text={confLabel(win, r.confidence)} tokens={ctx.tokens} />
        </span>
      ),
    },
    {
      key: '_catsText',
      label: t('col.categories'),
      sortable: true,
      filterText: (r) => catsCellData(r).text,
      render: (r, ctx) => {
        const c = catsCellData(r);
        return (
          <>
            {c.shown.map((cat, i) => (
              <React.Fragment key={i}>
                {i > 0 ? ' ' : null}
                <code className="bg-slate-100 px-1 rounded text-[10px]"><ctx.Mark text={cat} tokens={ctx.tokens} /></code>
              </React.Fragment>
            ))}
            {c.more > 0 && <span className="text-[10px] text-slate-500 ml-1">{'+' + c.more}</span>}
          </>
        );
      },
    },
    {
      key: '_tagsText',
      label: t('col.tags'),
      sortable: true,
      filterText: (r) => tagsCellData(r).text,
      render: (r, ctx) => {
        const tg = tagsCellData(r);
        return (
          <>
            {tg.shown.map((p, i) => (
              <React.Fragment key={i}>
                {i > 0 ? ' ' : null}
                <span className={p.color + ' text-[10px] px-1.5 py-0.5 rounded inline-block'}>
                  <ctx.Mark text={p.body} tokens={ctx.tokens} />
                  {p.scope ? <span className="opacity-60 font-normal"><ctx.Mark text={' @ ' + p.scope} tokens={ctx.tokens} /></span> : null}
                </span>
              </React.Fragment>
            ))}
            {tg.more > 0 && <span className="text-[10px] text-slate-500 ml-1">{'+' + tg.more}</span>}
          </>
        );
      },
    },
    {
      key: '_contactsCount',
      label: t('col.contacts'),
      sortable: true,
      sortValue: (r) => Number(r._contactsCount) || 0, // parità type:'number' v1
    },
    {
      key: 'createdAt',
      label: TEXTS.addedCol,
      sortable: true,
      sortValue: (r) => (r.createdAt ? new Date(r.createdAt).getTime() : 0), // parità type:'date' v1
      filterText: (r) => (r.createdAt ? fmtCreated(r.createdAt) : '—'),
      render: (r, ctx) => (
        <span className="text-xs text-slate-500 whitespace-nowrap">
          <ctx.Mark text={r.createdAt ? fmtCreated(r.createdAt) : '—'} tokens={ctx.tokens} />
        </span>
      ),
    },
  ];

  return (
    <div ref={rootRef}>
      <div className="flex justify-between items-center mb-3">
        <div>
          <h1 className="text-2xl font-bold">{t('sup.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{TEXTS.subtitle}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            id="btn-import-suppliers"
            type="button"
            className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-1.5 rounded text-sm"
            onClick={() => openBulkImport(win)}
          >{t('sup.bulk_import')}</button>
          <button
            id="btn-new-supplier"
            type="button"
            className="bg-slate-900 hover:bg-slate-800 text-white px-3 py-1.5 rounded text-sm"
            onClick={() => openNewSupplier(win)}
          >{t('sup.new')}</button>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3 mb-3 flex flex-wrap gap-2 items-center text-sm filter-bar">
        <select
          id="sup-filter-qual"
          className="border rounded px-2 py-1.5"
          value={f.qualified}
          onChange={(e) => setF({ qualified: e.target.value })}
        >
          <option value="all">{t('sup.all_filter')}</option>
          <option value="qualified">{t('sup.qualified_filter')}</option>
          <option value="prospect">{t('sup.prospect_filter')}</option>
        </select>
        {/* o17: bug v1:6581 CORRETTO in ENTRAMBI i mondi — value CANONICO
            'High'/'Medium'/'Low' (testo visibile tradotto). Prima le option
            senza value inviavano il testo tradotto e in it il filtro non
            matchava mai listSuppliers ('Alta' !== 'High'): la parità v1/v3
            è ora sul comportamento CORRETTO (pin nel contract test o17). */}
        <select
          id="sup-filter-conf"
          className="border rounded px-2 py-1.5"
          value={f.confidence}
          onChange={(e) => setF({ confidence: e.target.value })}
        >
          <option value="all">{t('sup.any_confidence')}</option>
          <option value="High">{t('sup.confidence.high')}</option>
          <option value="Medium">{t('sup.confidence.medium')}</option>
          <option value="Low">{t('sup.confidence.low')}</option>
        </select>
        <CategoryFilter
          win={win}
          cats={data.cats}
          value={f.category}
          onChange={(v) => setF({ category: v })}
        />
        <label className={'flex items-center gap-1 text-xs ml-2 ' + (f.sleeping ? 'text-amber-700 font-medium' : 'text-slate-600')}>
          <input
            type="checkbox"
            id="sup-filter-sleep"
            checked={f.sleeping}
            onChange={(e) => setF({ sleeping: e.target.checked })}
          /> {t('sup.sleeping')}
        </label>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" id="sup-table-mount">
        <DataTable
          columns={columns}
          rows={data.rows}
          rowKey="id"
          sort={{ key: 'createdAt', dir: 'desc' }}
          filterable
          selectable
          selection={selection}
          resolveSelection={(ids) => resolveSelection(win, ids)}
          bulkActions={buildBulkActions(win)}
          onBulkDone={() => { selection.clear(); force(); }}
          onRowClick={(row) => openSupplierDetail(win, row.id)}
          emptyMessage={TEXTS.emptyMessage}
        />
      </div>
    </div>
  );
}

// Factory (pattern createSopV3): il register/install la aggancia al WRAP di
// window.renderSuppliers. render() è idempotente sul container; unmount()
// libera il container (il monolite resta vivo per le altre viste).
export function createProcV3Suppliers(win) {
  let root = null;
  let mountedEl = null;
  let tick = 0;
  // stato di vista a livello di PAGINA, come supFilters/supplierSelection v1
  const filters = defaultFilters();
  const selection = new Set();

  function render() {
    const container = win.document.getElementById('suppliers-content');
    if (!container) throw new Error('#suppliers-content assente');
    if (!root || mountedEl !== container || !container.isConnected) {
      if (root) { try { root.unmount(); } catch (e) { /* già smontato */ } }
      // eventuali resti del v1 (catch-up): il root React parte da container pulito
      container.replaceChildren();
      root = win.ReactDOM.createRoot(container);
      mountedEl = container;
    }
    tick += 1; // ogni chiamata del wrapper = refresh dati (rerenderAll, modali v1, rientro)
    root.render(
      <SuppliersList win={win} tick={tick} filters={filters} selection={selection} />
    );
  }

  function unmount() {
    if (root) {
      try { root.unmount(); } catch (e) { /* già smontato */ }
      root = null;
    }
    mountedEl = null;
    // filters e selection NON si azzerano: parità col v1 (binding di pagina)
  }

  function isMounted() { return !!root; }

  return { render, unmount, isMounted };
}
